import {
  CurrencyFormatted,
  TooltipContent,
  TooltipTrigger,
  DateFormatted,
  Tooltip,
  Button,
  Badge,
  toast,
} from '@ui/components';
import { ColumnConfigurationColumnTypeEnum } from '@interfaces/models';
import { useCurrentUser, useGetFile } from '@shared/hooks';
import { DateTimeFormatOptions, DateTime } from 'luxon';
import { formatPhoneNumber } from '@helpers/utils';
import { InvoiceType } from '@interfaces/types';
import { useTranslation } from 'react-i18next';
import { ReactNode } from 'react';

type CellTemplate = (value?: unknown) => ReactNode | unknown;

const STRING: CellTemplate = (value?: unknown) => {
  return value ? (
    <Tooltip>
      <TooltipTrigger asChild>
        <span>{value as string}</span>
      </TooltipTrigger>
      <TooltipContent>{value as string}</TooltipContent>
    </Tooltip>
  ) : (
    '-'
  );
};

const DATE = (format: keyof typeof DateTime | DateTimeFormatOptions, value?: unknown) =>
  value ? <DateFormatted date={value as string} format={format} /> : '-';

const PHONE_NUMBER: CellTemplate = (value?: unknown) =>
  value ? formatPhoneNumber((value as string).slice(2), `+${(value as string).slice(0, 2)}`) : '-';

const LABELS = (value?: unknown) => {
  const stringifiedLabels = value as string;
  if (!stringifiedLabels) return '-';

  const labels = stringifiedLabels.split(';');

  const labelsCount = labels.length;

  return (
    <div className="flex items-center gap-1 whitespace-nowrap">
      <Badge size="large" outlined>
        {labels[0]}
      </Badge>
      {labelsCount > 1 && (
        <Tooltip>
          <TooltipTrigger>
            <Badge size="large" outlined>
              +{labelsCount - 1}
            </Badge>
          </TooltipTrigger>
          <TooltipContent className="bg-background flex gap-2 w-auto max-w-[15rem] flex-wrap">
            {labels.map((label) => (
              <Badge size="large" key={label} outlined>
                {label}
              </Badge>
            ))}
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
};

// change type to DOWNLOAD_INVOICE_ATTACHMENTS
const DOWNLOAD_LINK = (value: string) => {
  const { t } = useTranslation();

  const { handleGetFile, isLoading } = useGetFile({
    onSuccess: () => {
      toast.success(t('CELL_TEMPLATES.DOWNLOAD_SUCCESS'));
    },
  });

  const [bucketName, objectName] = value.split('|');
  if (!bucketName || !objectName) return '-';

  return (
    <Button
      onClick={() => handleGetFile({ objectName, bucketName })}
      isLoading={isLoading}
      disabled={isLoading}
      variant="link"
    >
      {t('CELL_TEMPLATES.DOWNLOAD')}
    </Button>
  );
};

const TOP_AMOUNT: CellTemplate = (value?: unknown) => {
  const { currency } = useCurrentUser();

  return value ? (
    <div className="flex gap-1 justify-end text-success">
      <span>+</span>
      <CurrencyFormatted value={value as number} currency={currency} />
    </div>
  ) : (
    '-'
  );
};

const CURRENCY: CellTemplate = (value?: unknown) => {
  const { currency } = useCurrentUser();

  return value ? <CurrencyFormatted value={value as number} currency={currency} /> : '-';
};

const INVOICE_BADGE = (value?: InvoiceType) => {
  const { t } = useTranslation();

  return value ? (
    <Badge variant={value === InvoiceType.INVOICE ? 'green' : 'blue'} size="large">
      {t(`FILES_REPORTS.INVOICING.${value}`)}
    </Badge>
  ) : (
    '-'
  );
};

export const cellTemplates: Record<ColumnConfigurationColumnTypeEnum, CellTemplate> = {
  [ColumnConfigurationColumnTypeEnum.INVOICE_BADGE]: (value) => INVOICE_BADGE(value as InvoiceType),
  [ColumnConfigurationColumnTypeEnum.ISO_DATE_WITH_TIME]: (value) => DATE('DATETIME_SHORT', value),
  [ColumnConfigurationColumnTypeEnum.DOWNLOAD_LINK]: (value) => DOWNLOAD_LINK(value as string),
  [ColumnConfigurationColumnTypeEnum.LOCAL_DATE]: (value) => DATE('DATE_SHORT', value),
  [ColumnConfigurationColumnTypeEnum.PHONE_NUMBER]: PHONE_NUMBER,
  [ColumnConfigurationColumnTypeEnum.TOP_UP_AMOUNT]: TOP_AMOUNT,
  [ColumnConfigurationColumnTypeEnum.STRING_CONCAT]: STRING,
  [ColumnConfigurationColumnTypeEnum.CURRENCY]: CURRENCY,
  [ColumnConfigurationColumnTypeEnum.LABELS]: LABELS,
  [ColumnConfigurationColumnTypeEnum.STRING]: STRING,
} as const;
